import { MIN_SPACE_DIM_FEET } from 'data/constants';
import { PositionAndDimensionsType, PositionType } from 'data/types';
import polygonClipping, {
  MultiPolygon,
  Polygon as ClipPolygon
} from 'polygon-clipping';
import { isRectInPolygon, polygonBoundingBox } from './footprint';
import { polygonsOverlapStrict, rectToAbsolutePolygon } from './space-polygon';

const STRIP_DEGENERATE_EPS = 1e-9;
const STRIP_PADDING_FT = 1;
const OPEN_AREA_FIT_MIN_DIM = MIN_SPACE_DIM_FEET;

type Axis = 'x' | 'y';
type Interval = { min: number; max: number };

function polygonToClipping(vertices: PositionType[]): ClipPolygon {
  const ring = vertices.map((v) => [v.x, v.y] as [number, number]);
  return [[...ring, ring[0]]];
}

function rectRingClipping(
  xMin: number,
  yMin: number,
  xMax: number,
  yMax: number
): ClipPolygon {
  return [
    [
      [xMin, yMin],
      [xMax, yMin],
      [xMax, yMax],
      [xMin, yMax],
      [xMin, yMin]
    ]
  ];
}

function polygonExtremes(polygon: PositionType[]): {
  xMin: number;
  xMax: number;
  yMin: number;
  yMax: number;
} | null {
  if (polygon.length === 0) return null;
  let xMin = Infinity;
  let xMax = -Infinity;
  let yMin = Infinity;
  let yMax = -Infinity;
  for (const v of polygon) {
    if (v.x < xMin) xMin = v.x;
    if (v.x > xMax) xMax = v.x;
    if (v.y < yMin) yMin = v.y;
    if (v.y > yMax) yMax = v.y;
  }
  if (!Number.isFinite(xMin) || !Number.isFinite(yMax)) return null;
  return { xMin, xMax, yMin, yMax };
}

function clipPolygonToHorizontalStrip(
  polygon: PositionType[],
  yMin: number,
  yMax: number
): MultiPolygon | null {
  const extremes = polygonExtremes(polygon);
  if (!extremes) return null;
  const strip = rectRingClipping(
    extremes.xMin - STRIP_PADDING_FT,
    yMin,
    extremes.xMax + STRIP_PADDING_FT,
    yMax
  );
  return polygonClipping.intersection(polygonToClipping(polygon), strip);
}

function clipPolygonToVerticalStrip(
  polygon: PositionType[],
  xMin: number,
  xMax: number
): MultiPolygon | null {
  const extremes = polygonExtremes(polygon);
  if (!extremes) return null;
  const strip = rectRingClipping(
    xMin,
    extremes.yMin - STRIP_PADDING_FT,
    xMax,
    extremes.yMax + STRIP_PADDING_FT
  );
  return polygonClipping.intersection(polygonToClipping(polygon), strip);
}

function extractAxisIntervals(
  multiPolygon: MultiPolygon,
  axis: Axis
): Interval[] {
  const intervals: Interval[] = [];
  for (const poly of multiPolygon) {
    const outer = poly[0];
    if (outer && outer.length >= 4) {
      let min = Infinity;
      let max = -Infinity;
      for (const [x, y] of outer) {
        const v = axis === 'x' ? x : y;
        if (v < min) min = v;
        if (v > max) max = v;
      }
      if (
        Number.isFinite(min) &&
        Number.isFinite(max) &&
        max > min + STRIP_DEGENERATE_EPS
      ) {
        intervals.push({ min, max });
      }
    }
  }
  return intervals;
}

export function getPolygonHorizontalIntervals(
  polygon: PositionType[],
  yMin: number,
  yMax: number
): Interval[] {
  if (polygon.length < 3 || yMax - yMin < STRIP_DEGENERATE_EPS) return [];
  const mp = clipPolygonToHorizontalStrip(polygon, yMin, yMax);
  if (!mp) return [];
  return extractAxisIntervals(mp, 'x');
}

export function getPolygonVerticalIntervals(
  polygon: PositionType[],
  xMin: number,
  xMax: number
): Interval[] {
  if (polygon.length < 3 || xMax - xMin < STRIP_DEGENERATE_EPS) return [];
  const mp = clipPolygonToVerticalStrip(polygon, xMin, xMax);
  if (!mp) return [];
  return extractAxisIntervals(mp, 'y');
}

export function polygonsOverlapRect(
  rect: PositionAndDimensionsType,
  polygons: PositionType[][]
): boolean {
  if (polygons.length === 0) return false;
  const rp = rectToAbsolutePolygon(rect);
  for (let i = 0; i < polygons.length; i += 1) {
    const p = polygons[i];
    if (p.length >= 3 && polygonsOverlapStrict(rp, p)) {
      return true;
    }
  }
  return false;
}

export function applyPolygonHorizontalShrink(
  polygon: PositionType[],
  yMin: number,
  yMax: number,
  refCenterX: number,
  left: number,
  right: number
): { left: number; right: number } {
  const intervals = getPolygonHorizontalIntervals(polygon, yMin, yMax);
  let newLeft = left;
  let newRight = right;
  for (const { min, max } of intervals) {
    const mid = (min + max) / 2;
    if (mid < refCenterX) {
      newLeft = Math.max(newLeft, max);
    } else {
      newRight = Math.min(newRight, min);
    }
  }
  return { left: newLeft, right: newRight };
}

export function applyPolygonVerticalShrink(
  polygon: PositionType[],
  xMin: number,
  xMax: number,
  refCenterY: number,
  top: number,
  bottom: number
): { top: number; bottom: number } {
  const intervals = getPolygonVerticalIntervals(polygon, xMin, xMax);
  let newTop = top;
  let newBottom = bottom;
  for (const { min, max } of intervals) {
    const mid = (min + max) / 2;
    if (mid < refCenterY) {
      newTop = Math.max(newTop, max);
    } else {
      newBottom = Math.min(newBottom, min);
    }
  }
  return { top: newTop, bottom: newBottom };
}

function computeOpenAreaComponents(
  obstaclePolygons: PositionType[][],
  footprint: PositionType[]
): PositionType[][] {
  if (footprint.length < 3) return [];
  const footprintClip = polygonToClipping(footprint);
  const validObstacles = obstaclePolygons.filter((p) => p.length >= 3);
  if (validObstacles.length === 0) {
    return [footprint];
  }
  const obstacleClips = validObstacles.map(polygonToClipping);
  const [firstObstacle, ...restObstacles] = obstacleClips;
  const obstaclesUnion = polygonClipping.union(firstObstacle, ...restObstacles);
  const openArea = polygonClipping.difference(footprintClip, ...obstaclesUnion);
  const components: PositionType[][] = [];
  for (const poly of openArea) {
    const outer = poly[0];
    if (outer && outer.length >= 4) {
      const verts: PositionType[] = [];
      for (let i = 0; i < outer.length - 1; i += 1) {
        const [x, y] = outer[i];
        verts.push({ x, y });
      }
      if (verts.length >= 3) {
        components.push(verts);
      }
    }
  }
  return components;
}

function buildBboxFitRect(
  rect: PositionAndDimensionsType,
  bb: { minX: number; maxX: number; minY: number; maxY: number }
): PositionAndDimensionsType {
  const bboxWidth = bb.maxX - bb.minX;
  const bboxHeight = bb.maxY - bb.minY;
  const fitWidth = Math.min(rect.width, bboxWidth);
  const fitHeight = Math.min(rect.height, bboxHeight);
  const centerX = rect.x + rect.width / 2;
  const centerY = rect.y + rect.height / 2;
  const desiredX = centerX - fitWidth / 2;
  const desiredY = centerY - fitHeight / 2;
  const clampedX = Math.max(bb.minX, Math.min(bb.maxX - fitWidth, desiredX));
  const clampedY = Math.max(bb.minY, Math.min(bb.maxY - fitHeight, desiredY));
  return {
    x: clampedX,
    y: clampedY,
    width: fitWidth,
    height: fitHeight
  };
}

function rectClearsObstacles(
  rect: PositionAndDimensionsType,
  obstaclePolygons: PositionType[][]
): boolean {
  if (obstaclePolygons.length === 0) return true;
  const rectPolygon = rectToAbsolutePolygon(rect);
  for (const p of obstaclePolygons) {
    if (p.length >= 3 && polygonsOverlapStrict(rectPolygon, p)) {
      return false;
    }
  }
  return true;
}

function ringSignedArea(ring: [number, number][]): number {
  let area = 0;
  const n = ring.length - 1;
  for (let i = 0; i < n; i += 1) {
    const [x1, y1] = ring[i];
    const [x2, y2] = ring[(i + 1) % n];
    area += x1 * y2 - x2 * y1;
  }
  return area / 2;
}

function multiPolygonArea(mp: MultiPolygon): number {
  let area = 0;
  for (const poly of mp) {
    for (let r = 0; r < poly.length; r += 1) {
      const ring = poly[r];
      if (ring && ring.length >= 4) {
        const signed = ringSignedArea(ring);
        area += r === 0 ? Math.abs(signed) : -Math.abs(signed);
      }
    }
  }
  return area;
}

function findBestOverlappingComponent(
  components: PositionType[][],
  rect: PositionAndDimensionsType
): PositionType[] | null {
  if (components.length === 0) return null;
  const rectClip = polygonToClipping(rectToAbsolutePolygon(rect));
  let best: PositionType[] | null = null;
  let bestArea = 0;
  for (const component of components) {
    const componentClip = polygonToClipping(component);
    const intersection = polygonClipping.intersection(rectClip, componentClip);
    const area = multiPolygonArea(intersection);
    if (area > bestArea) {
      bestArea = area;
      best = component;
    }
  }
  return best;
}

export function findOpenAreaFit(
  rect: PositionAndDimensionsType,
  obstaclePolygons: PositionType[][],
  footprint: PositionType[]
): PositionAndDimensionsType | null {
  if (footprint.length < 3) return null;

  const components = computeOpenAreaComponents(obstaclePolygons, footprint);
  if (components.length === 0) return null;

  const bestComponent = findBestOverlappingComponent(components, rect);
  if (!bestComponent) return null;

  const bb = polygonBoundingBox(bestComponent);
  const fitRect = buildBboxFitRect(rect, bb);

  if (
    fitRect.width < OPEN_AREA_FIT_MIN_DIM ||
    fitRect.height < OPEN_AREA_FIT_MIN_DIM
  ) {
    return null;
  }

  if (!isRectInPolygon(fitRect, footprint)) return null;
  if (!rectClearsObstacles(fitRect, obstaclePolygons)) return null;

  return fitRect;
}
